import React from 'react';
import { Modal, Stack, Button, ActionIcon, Box, useMantineTheme, useComputedColorScheme } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { MenuItem as MenuItemType } from '../../types/menu';
import { useCart } from '../../context/CartContext';
import { MenuItemDetails } from './MenuItemDetails';
import { t } from '../../utils/uiTranslations';

interface MenuItemModalProps {
  opened: boolean;
  onClose: () => void;
  item: MenuItemType | null;
  getDisplayText: (item: MenuItemType, field: 'name' | 'description' | 'disclaimer') => string;
  menuId?: string;
  restaurantName?: string;
  restaurantDescription?: string;
  currentLanguage?: string;
  /** When false, hide the Add to Order button. Defaults to true. */
  cartEnabled?: boolean;
}

export const MenuItemModal: React.FC<MenuItemModalProps> = ({
  opened,
  onClose,
  item,
  getDisplayText,
  menuId,
  restaurantName,
  restaurantDescription,
  currentLanguage = 'en',
  cartEnabled = true
}) => {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';
  const { addItem } = useCart();
  const modalZIndex = (theme.other?.layout?.zIndex?.notifications ?? 10) + 5;

  if (!item) return null;

  const handleUpdateCart = () => {
    addItem(item);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      radius="md"
      size="xl"
      zIndex={modalZIndex}
      overlayProps={{ blur: 4, opacity: 0.5 }}
      styles={{
        body: {
          padding: 0,
          maxHeight: '88vh',
          overflowY: 'auto',
          backgroundColor: isDarkMode ? theme.colors.dark[7] : undefined
        },
        content: {
          maxHeight: '88vh',
          overflow: 'hidden',
          backgroundColor: isDarkMode ? theme.colors.dark[7] : undefined
        },
        inner: {
          display: 'flex',
          alignItems: 'center'
        }
      }}
      transitionProps={{ transition: 'fade', duration: 150 }}
    >
      <Box style={{ position: 'relative' }}>
        <ActionIcon
          aria-label="Close menu item details"
          onClick={onClose}
          variant="transparent"
          size="lg"
          radius="xl"
          style={{
            position: 'absolute',
            top: theme.spacing.sm,
            right: theme.spacing.sm,
            zIndex: 3,
            color: isDarkMode ? theme.white : theme.black,
            backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.72)',
            backdropFilter: 'blur(4px)',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)'}`
          }}
        >
          <IconX size={20} stroke={2} />
        </ActionIcon>

        <MenuItemDetails
          item={item}
          getDisplayText={getDisplayText}
          menuId={menuId}
          restaurantName={restaurantName}
          restaurantDescription={restaurantDescription}
          currentLanguage={currentLanguage}
        />
      </Box>

      {cartEnabled && (
        <Box
          style={{
            paddingTop: theme.spacing.md,
            paddingBottom: theme.spacing.lg,
            backgroundColor: isDarkMode ? theme.colors.dark[7] : theme.white,
            borderTop: `1px solid ${isDarkMode ? theme.colors.dark[5] : theme.colors.gray[2]}`,
            marginTop: 'auto'
          }}
        >
          <Stack gap="md">
            <Button
              variant="filled"
              color="orange"
              onClick={handleUpdateCart}
              fullWidth
            >
              {t('addToOrder', currentLanguage)}
            </Button>
          </Stack>
        </Box>
      )}
    </Modal>
  );
};
